/**
 * Validates a post-login redirect target against an open-redirect attack.
 *
 * The `redirect` query param on `/portal/sign-in` is captured by
 * RequireAuth from whatever URL the unauthenticated user was trying to
 * reach. That value reaches the sign-in form via the URL search string,
 * which means it's user-controllable: an attacker who can persuade a
 * victim to click a crafted link like
 *   /portal/sign-in?redirect=https://evil.example.com
 * could land them on attacker-controlled content the moment they sign in,
 * giving the attacker a believable surface to phish credentials or run
 * other follow-on attacks.
 *
 * Whitelist rather than blacklist:
 *   - must start with `/portal/` so it can only land inside the
 *     authenticated portal area (RequireAuth only fires there anyway)
 *   - reject `//` after the leading slash - `//evil.com/x` is a
 *     protocol-relative URL most browsers resolve cross-origin
 *   - reject backslashes - some browsers normalise `\\` to `/`, which
 *     bypasses naive same-origin checks
 *   - reject control characters as defence in depth against
 *     newline-style header injection if this ever flows somewhere
 *     unsafe; legitimate URL paths never contain them
 */
/**
 * Login-gated pages that live OUTSIDE `/portal/`.
 *
 * `/oauth/consent` is where CP's `/api/v1/oauth/authorize` sends the browser
 * (`CONSENT_PATH` in `control_plane/api/oauth.py`), and it is login-gated like
 * any portal page — so an unauthenticated arrival has to be able to name it as
 * its post-login target. Without this entry the guard drops the redirect, the
 * user lands on `/portal` after signing in, and the OAuth flow they were in the
 * middle of is silently abandoned: the parked request expires and their Claude
 * reports nothing but a timeout.
 *
 * Exact paths only, compared after the query string is stripped — a prefix rule
 * here would re-open the hole the `/portal/` allow-list exists to close.
 */
const EXACT_ALLOWED_PATHS: ReadonlySet<string> = new Set(["/oauth/consent"]);

export function isSafePortalRedirect(target: unknown): target is string {
  if (typeof target !== "string") return false;
  if (target.startsWith("//") || target.includes("\\")) return false;
  // Reject control characters (NUL through US, 0x00-0x1F). Char-code check
  // rather than a regex with literal control chars so the source stays
  // readable in editors and diff tools. Hoisted above the allow-lists so the
  // exact-path allowance below cannot be reached by a value that would fail
  // it (e.g. "/oauth/consent\nevil").
  for (let i = 0; i < target.length; i++) {
    if (target.charCodeAt(i) < 0x20) return false;
  }
  if (EXACT_ALLOWED_PATHS.has(target.split("?", 1)[0]!)) return true;
  if (!target.startsWith("/portal/")) return false;
  if (target.startsWith("/portal//")) return false;
  // Reject the auth routes themselves — otherwise a recursive
  // ``redirect=/portal/sign-in?...`` builds the user a redirect loop
  // on every post-login navigate(). Take only the pathname portion
  // when checking so query strings (legitimate deep links) survive.
  const pathOnly = target.split("?", 1)[0]!;
  if (
    pathOnly === "/portal/sign-in" ||
    pathOnly === "/portal/sign-up" ||
    pathOnly.startsWith("/portal/sign-in/") ||
    pathOnly.startsWith("/portal/sign-up/") ||
    pathOnly === "/portal/reset-password" ||
    pathOnly.startsWith("/portal/reset-password/")
  ) {
    return false;
  }
  return true;
}
